/**
 * 복사 알림 — v2.317.0 부터 공용 토스트(lib/toast.ts)로 넘긴다. 부르는 쪽 이름(showToast·copyWithToast)은 그대로.
 *   (2026-09-03 처음 만들 때는 복사 전용 DOM 하나를 재사용했다 · 이제 한 벌의 토스트가 네 종류·5초·× 를 맡는다)
 */
import { notify } from './toast';

export function showToast({
  strong,
  text,
  tone = 'ok',
}: {
  /** 굵게 보여 줄 값 (예: 복사한 프로젝트명). 사용자 데이터라 textContent 로만 넣는다. */
  strong?: string;
  text: string;
  tone?: 'ok' | 'error';
}): void {
  // 복사 알림은 짧게(2.2초) — 연달아 복사할 때 오래 남지 않게
  notify(tone, text, { strong, ms: 2200 });
}

/**
 * 글자를 클립보드에 복사하고 안내 알림을 띄운다.
 *
 * navigator.clipboard 는 보안 컨텍스트(https·localhost)에서만 동작하므로
 * 실패하면 숨긴 textarea + execCommand 로 한 번 더 시도한다.
 * 둘 다 실패하면 **성공한 척하지 않고** 빨간 알림으로 알린다.
 *
 * ⭐**실제로 복사됐는지를 돌려준다**(v2.181.0). 부르는 쪽이 복사 성공에 무언가를 잇는다면
 *   이 값을 반드시 볼 것 — 안 보면 「복사하지 못했습니다」를 띄워 놓고 뒷일은 해 버리는
 *   거짓말이 된다(개발 계획의 「복사하면 전달함으로」가 그 자리였다).
 */
export async function copyWithToast(
  value: string,
  what: string,
  /**
   * 알림에 굵게 보여 줄 글 — 안 주면 복사한 값을 그대로 보여 준다.
   * ⭐**긴 글을 복사할 때 필요하다**(개발 계획 글 등) — 값을 그대로 띄우면 알림이 화면을 덮는다.
   *   `''` 를 주면 굵은 줄 없이 안내 문구만 보인다.
   */
  opts: { strong?: string } = {},
): Promise<boolean> {
  const ok = await copyText(value);
  const w = what + eulReul(what);
  if (ok) {
    showToast({ strong: opts.strong ?? value, text: `${w} 클립보드에 복사하였습니다.` });
  } else {
    showToast({
      text: `복사하지 못했습니다. ${w} 마우스로 직접 선택해 복사해 주세요.`,
      tone: 'error',
    });
  }
  return ok;
}

/** 받침 유무에 따라 을/를 을 고른다 (한글이 아니면 '을'). */
function eulReul(word: string): string {
  const last = word.trim().slice(-1);
  const code = last.charCodeAt(0);
  if (code < 0xac00 || code > 0xd7a3) return '을';
  return (code - 0xac00) % 28 === 0 ? '를' : '을';
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* 아래 대체 경로로 */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
